// Entry router. index.html loads THIS file, and its job is to decide — before importing anything
// expensive — whether this page is the Designer or the cross-device preview receiver.
//
// WHY THIS FILE EXISTS AT ALL. The routing used to live inside the Designer's own entry module, which
// looked fine but couldn't work: ES imports all resolve before any module body runs, so `./store/index`
// → reducers → the `@/utils` barrel → EditorCanvas → three.js/zappar-cv had to load BEFORE the route
// check executed. In a production build that's a wasted ~3.4 MB chunk. In dev it is far worse: Vite
// serves every source module as its own request, so a phone opening the receiver had to fetch the whole
// Designer graph over the tunnel first, sitting on the static splash in index.html the entire time —
// which is exactly what "the phone still opens the Zapworks splash page" was.
//
// So: keep this file's static imports to react/react-dom plus the pure route matcher, and load each
// branch dynamically. Nothing added here may pull in the store, the sync layer, settings, or anything
// under components/ other than through a dynamic import.

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { matchRemotePreviewRoute } from './preview/webrtc/remote-preview-routes';

const RemotePreviewReceiver = React.lazy(() => import('./components/dom/preview/RemotePreviewReceiver/RemotePreviewReceiver'));

/**
 * The healthy-path screen: a spinner, and nothing else.
 *
 * Getting a preview onto a phone used to walk through four screens before JUTE appeared — a boot panel, a
 * "connecting" panel, and (where the Permissions API reports 'prompt') a camera step — each of which the
 * user could do nothing about. They are collapsed into this: splash → spinner → JUTE. Diagnostics are
 * still available, but only when something has actually gone wrong (see ReceiverBootScreen).
 *
 * The animation is SVG `animateTransform`, not CSS: this file must stay dependency-free, so there is no
 * stylesheet to declare a keyframe in. `#5276F7` is `COLORS.nebula`, hardcoded for the same reason.
 */
function ReceiverSpinner() {
    return <div style={{
        display: 'flex',
        width: '100vw',
        height: '100dvh',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F6F6F9',
    }}>
        <svg width="40" height="40" viewBox="0 0 40 40" role="progressbar" aria-label="Loading preview">
            <circle cx="20" cy="20" r="16" fill="none" stroke="#DDE3F5" strokeWidth="4" />
            <path d="M20 4 a16 16 0 0 1 16 16" fill="none" stroke="#5276F7" strokeWidth="4" strokeLinecap="round">
                <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 20 20"
                    to="360 20 20"
                    dur="0.8s"
                    repeatCount="indefinite"
                />
            </path>
        </svg>
    </div>;
}

/**
 * Diagnostic screen, shown ONLY on a real failure — a routing/service-worker problem or a crash.
 *
 * Deliberately dependency-free (inline styles, no design tokens, no Redux): it renders on a phone with
 * none of the app booted, and on a phone there is no console to open — so it reports the facts needed to
 * tell a routing problem from a transport one.
 */
function ReceiverBootScreen({ message }: { message: string }) {
    const facts = [
        `path: ${location.pathname}`,
        `pid: ${new URLSearchParams(location.search).get('pid') ?? '(missing)'}`,
        `secure context: ${window.isSecureContext ? 'yes' : 'NO — service workers are unavailable'}`,
        `service workers: ${'serviceWorker' in navigator ? 'supported' : 'NOT supported'}`
    ];
    return <div style={{
        display: 'flex',
        width: '100vw',
        height: '100dvh',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        backgroundColor: '#F6F6F9',
        fontFamily: 'Lato, sans-serif',
    }}>
        <div style={{
            maxWidth: '360px',
            padding: '32px 24px',
            borderRadius: '11px',
            backgroundColor: 'white',
            color: '#073158',
            boxShadow: '0 2px 4px 0 rgba(0,0,0,0.16)',
            textAlign: 'center',
        }}>
            <h1 style={{ margin: '0 0 16px', fontSize: '20px' }}>Live preview</h1>
            <p style={{ margin: '0 0 16px', fontSize: '14px', lineHeight: 1.4 }}>{message}</p>
            <div style={{ fontSize: '11px', color: '#9b9b9b', textAlign: 'left', lineHeight: 1.6, wordBreak: 'break-all' }}>
                {facts.map((f) => <div key={f}><code>{f}</code></div>)}
            </div>
        </div>
    </div>;
}

/** Plain boundary rather than Sentry's: importing @sentry/react here would defeat the point of this
 *  file. Sentry still initialises on the Designer path, inside designer-entry. */
class ReceiverErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
    state: { error: Error | null } = { error: null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    componentDidCatch(error: Error) {
        console.error('[webrtc-recv] receiver crashed', error);
    }

    render() {
        if (this.state.error) {
            return <ReceiverBootScreen message={`The preview failed to start: ${this.state.error.message}`} />;
        }
        return this.props.children;
    }
}

const route = matchRemotePreviewRoute(location.pathname);

if (route === 'app') {
    // The Designer, unchanged — everything that used to be in this file lives there now.
    void import('./designer-entry');
} else {
    const container = document.getElementById('root') || document.createElement('div');
    const root = createRoot(container);

    if (route === 'receiver') {
        console.log('[webrtc-recv] remote-preview route — rendering the receiver instead of Designer');
        root.render(
            // The Suspense fallback is deliberately VISIBLE rather than null. index.html ships a static
            // Zapworks splash inside #root, so a null fallback made three very different states look the
            // same on a phone with no console: (a) the app's JS never ran — splash just stays, (b) the
            // receiver chunk is still downloading, (c) the chunk failed. The spinner collapses that
            // ambiguity — if it is moving, JS ran and the route matched — without putting a panel of text
            // in front of someone who only wants to see their project.
            <ReceiverErrorBoundary>
                <React.Suspense fallback={<ReceiverSpinner />}>
                    <RemotePreviewReceiver />
                </React.Suspense>
            </ReceiverErrorBoundary>
        );
    } else {
        // 'sw-miss' — only the preview service worker should ever answer this path. The app being asked
        // to render it means the worker didn't, and the SPA fallback returned this shell with HTTP 200
        // instead. Say so, rather than booting the editor inside someone's preview frame.
        console.error(
            `[webrtc-recv] ${location.pathname} should have been served by the preview service worker. ` +
                'It was served by the SPA fallback instead, so the worker is not controlling its scope.'
        );
        root.render(
            <ReceiverBootScreen
                message="The preview service worker isn’t serving this session, so there is nothing to run. Scan the QR code in Designer again, or reload to re-register the service worker."
            />
        );
    }
}
